import { Injectable, inject } from '@angular/core';
import { Observable, map, withLatestFrom } from 'rxjs';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { TranslateService } from '@ngx-translate/core';
import { ProjectStore } from '../../state/project/project.store';
import { FormName, HeatLoadCalculationMethod } from '@nx-customer-apps/shared/enums';
import { BuildingHull, FormOptions } from '@nx-customer-apps/shared/interfaces';
import { LodashService } from '@nx-customer-apps/shared/services';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { HeatloadPrefillMapper } from '../../services';
import { AccordionService } from '../../shared/interfaces';
import { BuildingHullFormService } from '../../shared/components/building-hull';

@Injectable({
    providedIn: 'root'
})
export class BuildingHullService implements AccordionService {
    private prefillMapper = inject(HeatloadPrefillMapper);
    private heatloadFormOptionsService = inject(HeatloadFormOptionsService);
    private heatloadStore = inject(HeatloadStore);
    private translate = inject(TranslateService);
    private projectStore = inject(ProjectStore);
    private formService = inject(BuildingHullFormService);

    public questions$: Observable<any> = this.heatloadStore.calculationMethod$.pipe(
        withLatestFrom(this.projectStore.project$, this.heatloadStore.cache$),
        map(([method, project, cache]) => ({
            isBuildingHull: method?.value === HeatLoadCalculationMethod.BuildingHull,
            project,
            cache
        })),

        map(state => {
            if (!state.isBuildingHull) {
                return null;
            }

            const projectPrefill = this.prefillMapper.projectToBuildingHullPrefill(state.project!);
            const cachePrefill = this.prefillMapper.stateToBuildingHullPrefill(state.cache?.buildingHull);
            const options = this.heatloadFormOptionsService.getBuildingHullFormOptions(state.project!, cachePrefill || projectPrefill);
            return this.formService.getBuildingQuestions(options as FormOptions);
        })
    );

    public getSuccessMessage(formValue: BuildingHull): string {
        const options = {
            area: this.roundToFixed2(+formValue.buildingDimensions.buildingLength * +formValue.buildingDimensions.buildingWidth),
            numberOfFloors: formValue.buildingDimensions.numberOfFloors
        };
        return this.translate.instant('HEATLOAD.BUILDING.SUCCESS_MESSAGE', options);
    }

    public isExpanded$ = this.isSubmitedCalculationMethodSameValue$();

    private isSubmitedCalculationMethodSameValue$() {
        return this.heatloadStore.sameValueSubmited$.pipe(
            map(
                form =>
                    form?.formName === FormName.CalculationMethod &&
                    form.value.methods.standardMethod === HeatLoadCalculationMethod.BuildingHull
            )
        );
    }

    private roundToFixed2(value: number): number {
        if (LodashService.isNaN(value)) {
            return value;
        }
        return Math.round(value * 100) / 100;
    }
}
